/**
 * Hardware-grade device detection — resistant to spoofing from both sides:
 *
 *   • Desktop side: Chrome DevTools "Toggle device toolbar" fakes UA,
 *     pointer, hover, touchPoints, screen size — but NOT the GPU string
 *     or CPU/RAM counters.
 *   • Mobile side: Chrome/Safari "Request desktop site" fakes UA and
 *     can flip pointer/hover queries when combined with landscape +
 *     zoom-out (75%) — but the GPU string still reports Adreno / Mali /
 *     Apple A-series, and `maxTouchPoints` stays > 0.
 *
 * Decision rule
 * ─────────────
 *   1. Cached verdict wins (sticky for the whole session, both ways).
 *   2. Mobile GPU  → handheld, cache, done.
 *   3. Real touch hardware (`maxTouchPoints > 0`) + no desktop GPU
 *      → handheld, cache, done.   ← catches "Request desktop site"
 *   4. Desktop GPU OR (cores ≥ 8 AND memory ≥ 8 AND no touch)
 *      → desktop, cache, done.
 *   5. Legacy UA + pointer + hover fallback.
 *
 * Once a verdict is reached it is stored in `sessionStorage` so toggling
 * DevTools (on a laptop) or "Request desktop site" + rotate + zoom
 * (on a phone/tablet) mid-session cannot flip the UI.
 *
 * SSR-safe: first render returns `ready:false`; the real value lands
 * inside `useEffect`.
 */

import { useEffect, useState } from "react";

const CACHE_KEY = "rawanui:deviceClass"; // "desktop" | "handheld"

const MOBILE_UA = /android|webos|iphone|ipod|blackberry|iemobile|opera mini|mobile/i;
const TABLET_UA = /ipad|tablet|playbook|silk|kindle|nexus 7|nexus 10/i;

const DESKTOP_GPU =
  /(nvidia|geforce|quadro|radeon|amd |ati |intel.*(hd|uhd|iris|arc|graphics)|apple m\d|llvmpipe|swiftshader|angle.*(direct3d|d3d|metal|opengl))/i;
const MOBILE_GPU = /(adreno|mali-|powervr|apple a\d{1,2}|apple gpu|tegra|videocore)/i;

function readGpuRenderer(): string {
  try {
    const canvas = document.createElement("canvas");
    const gl = (canvas.getContext("webgl") ||
      canvas.getContext("experimental-webgl")) as WebGLRenderingContext | null;
    if (!gl) return "";
    const ext = gl.getExtension("WEBGL_debug_renderer_info");
    const renderer = ext
      ? (gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) as string)
      : (gl.getParameter(gl.RENDERER) as string);
    return String(renderer || "");
  } catch {
    return "";
  }
}

type DeviceState = { isDesktop: boolean; isMobile: boolean; isTablet: boolean };

function readCache(): "desktop" | "handheld" | null {
  try {
    const v = sessionStorage.getItem(CACHE_KEY);
    if (v === "desktop" || v === "handheld") return v;
  } catch {
    /* private mode */
  }
  return null;
}

function writeCache(v: "desktop" | "handheld") {
  try { sessionStorage.setItem(CACHE_KEY, v); } catch { /* ignore */ }
}

function classifyTablet(ua: string, touchPoints: number): boolean {
  const platform = (navigator as Navigator & { platform?: string }).platform || "";
  const isIPadOS = platform === "MacIntel" && touchPoints > 1;
  return TABLET_UA.test(ua) || isIPadOS;
}

function asHandheld(ua: string, touchPoints: number): DeviceState {
  const isTablet = classifyTablet(ua, touchPoints);
  return {
    isDesktop: false,
    isTablet,
    isMobile: !isTablet,
  };
}

function detect(): DeviceState {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return { isDesktop: false, isMobile: false, isTablet: false };
  }

  const ua = navigator.userAgent || "";
  const touchPoints = navigator.maxTouchPoints || 0;

  // 1) Sticky cache — once decided, stay decided for the session.
  const cached = readCache();
  if (cached === "desktop") return { isDesktop: true, isMobile: false, isTablet: false };
  if (cached === "handheld") return asHandheld(ua, touchPoints);

  // 2) Hardware signals.
  const gpu = readGpuRenderer();
  const cores = navigator.hardwareConcurrency || 0;
  const memory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory || 0;

  const gpuIsMobile = !!gpu && MOBILE_GPU.test(gpu);
  const gpuIsDesktop = !!gpu && DESKTOP_GPU.test(gpu) && !gpuIsMobile;

  // 2a) Mobile GPU → handheld. Beats every spoof.
  if (gpuIsMobile) {
    writeCache("handheld");
    return asHandheld(ua, touchPoints);
  }

  // 2b) Real touch hardware + not a desktop GPU → handheld.
  //     Catches "Request desktop site" on phone/tablet (UA flips, but
  //     maxTouchPoints stays > 0).
  if (touchPoints > 0 && !gpuIsDesktop) {
    writeCache("handheld");
    return asHandheld(ua, touchPoints);
  }

  // 2c) Desktop GPU, or strong CPU/RAM with no touch → desktop.
  if (gpuIsDesktop || (cores >= 8 && memory >= 8 && touchPoints === 0)) {
    writeCache("desktop");
    return { isDesktop: true, isMobile: false, isTablet: false };
  }

  // 3) Legacy fallback (UA + pointer + hover).
  const isTabletUA = classifyTablet(ua, touchPoints);
  const isMobileUA = MOBILE_UA.test(ua);
  if (isMobileUA || isTabletUA) {
    writeCache("handheld");
    return asHandheld(ua, touchPoints);
  }

  const hasFinePointer = window.matchMedia?.("(pointer: fine)").matches ?? false;
  const hasHover = window.matchMedia?.("(hover: hover)").matches ?? false;
  const isDesktop = hasFinePointer && hasHover && touchPoints === 0;

  if (isDesktop) {
    writeCache("desktop");
    return { isDesktop: true, isMobile: false, isTablet: false };
  }

  writeCache("handheld");
  return asHandheld(ua, touchPoints);
}

export function useDeviceClass() {
  const [state, setState] = useState(() => ({
    isDesktop: false,
    isMobile: false,
    isTablet: false,
    ready: false,
  }));

  useEffect(() => {
    // Run once. We deliberately do NOT re-run on (pointer:) / (hover:) /
    // resize / orientationchange — those are exactly the signals that
    // get spoofed by DevTools or "Request desktop site".
    setState({ ...detect(), ready: true });
  }, []);

  return state;
}

/** True ONLY on a real desktop/laptop computer. Sticky across spoofs. */
export function useIsDesktop(): boolean {
  return useDeviceClass().isDesktop;
}

/** True on phones / tablets (anything that is not a real desktop). */
export function useIsHandheld(): boolean {
  const { isDesktop, ready } = useDeviceClass();
  return ready && !isDesktop;
}
